import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth';
import { usePregnancyStore } from '@/store/pregnancyStore';
import { useTranslation } from '@parentpath/i18n';
import { parseSettingNumber } from '@/lib/systemSettings';
import { getMockResponse, getTypingDelay } from '../services/mockResponses';
import type { AIMessage, AIConversation, AIUsage, ChatSettings } from '../types';

const DEFAULT_SETTINGS: ChatSettings = {
  ai_chat_enabled: true,
  ai_chat_mode: 'mock',
  ai_questions_limit: 10, // Default for free tier, -1 = unlimited
  ai_max_tokens: 500,
};

interface UseAIChatReturn {
  messages: AIMessage[];
  conversation: AIConversation | null;
  usage: AIUsage | null;
  settings: ChatSettings;
  subscriptionTier: 'free' | 'premium';
  isLoading: boolean;
  isSending: boolean;
  error: string | null;
  sendMessage: (content: string) => Promise<void>;
  clearConversation: () => Promise<void>;
  isLimitReached: boolean;
  remainingQuestions: number;
  isUnlimited: boolean;
}

export function useAIChat(): UseAIChatReturn {
  const { user } = useAuth();
  const { currentPregnancy } = usePregnancyStore();
  const { t, i18n } = useTranslation('aiChat');

  const [messages, setMessages] = useState<AIMessage[]>([]);
  const [conversation, setConversation] = useState<AIConversation | null>(null);
  const [usage, setUsage] = useState<AIUsage | null>(null);
  const [settings, setSettings] = useState<ChatSettings>(DEFAULT_SETTINGS);
  const [subscriptionTier, setSubscriptionTier] = useState<'free' | 'premium'>('free');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // -1 means unlimited
  const isUnlimited = settings.ai_questions_limit === -1;

  // Calculate limit status (unlimited = never reached)
  const isLimitReached = isUnlimited
    ? false
    : (usage ? usage.questions_count >= settings.ai_questions_limit : false);

  const remainingQuestions = isUnlimited
    ? Infinity
    : (usage
        ? Math.max(0, settings.ai_questions_limit - usage.questions_count)
        : settings.ai_questions_limit);

  // Ref to track the latest load request and avoid race conditions
  const loadRequestRef = useRef(0);

  // Load user's subscription tier - returns the tier directly
  const loadSubscriptionTier = useCallback(async (): Promise<'free' | 'premium'> => {
    if (!user?.id) return 'free';

    try {
      const { data, error: tierError } = await supabase
        .from('profiles')
        .select('subscription_tier')
        .eq('id', user.id)
        .single();

      if (tierError) {
        console.error('Error loading subscription tier:', tierError);
        return 'free';
      }

      const tier = (data?.subscription_tier as 'free' | 'premium') || 'free';
      setSubscriptionTier(tier);
      return tier;
    } catch (err) {
      console.error('Error loading subscription tier:', err);
      return 'free';
    }
  }, [user?.id]);

  // Load settings from system_settings based on subscription tier
  const loadSettings = useCallback(async (tier: 'free' | 'premium') => {
    try {
      const limitKey = tier === 'premium'
        ? 'ai_questions_limit_premium'
        : 'ai_questions_limit_free';

      console.log(`[AI Chat] Loading settings for tier: ${tier}, limit key: ${limitKey}`);

      const { data, error: settingsError } = await supabase
        .from('system_settings')
        .select('key, value')
        .in('key', ['ai_chat_enabled', 'ai_chat_mode', limitKey, 'ai_max_tokens']);

      if (settingsError) {
        console.error('[AI Chat] Error loading settings:', settingsError);
        return;
      }

      console.log('[AI Chat] Raw settings data:', data);

      if (data) {
        const newSettings = { ...DEFAULT_SETTINGS };

        // For premium tier, default to unlimited (-1) if no setting found
        if (tier === 'premium') {
          newSettings.ai_questions_limit = -1;
        }

        // Check if limit key was found
        const limitSetting = data.find(s => s.key === limitKey);
        if (!limitSetting) {
          console.log(`[AI Chat] No setting found for ${limitKey}, using default: ${newSettings.ai_questions_limit}`);
        }

        data.forEach((setting) => {
          const rawValue = setting.value;
          console.log(`[AI Chat] Processing ${setting.key}: raw=${JSON.stringify(rawValue)}, type=${typeof rawValue}`);

          switch (setting.key) {
            case 'ai_chat_enabled':
              newSettings.ai_chat_enabled = rawValue === true || rawValue === 'true';
              break;
            case 'ai_chat_mode':
              // Handle both string and quoted string from JSONB
              const modeValue = typeof rawValue === 'string' ? rawValue.replace(/^"|"$/g, '') : rawValue;
              newSettings.ai_chat_mode = modeValue === 'api' ? 'api' : 'mock';
              break;
            case 'ai_questions_limit_free':
            case 'ai_questions_limit_premium':
              newSettings.ai_questions_limit = parseSettingNumber(rawValue, tier === 'premium' ? -1 : 10);
              break;
            case 'ai_max_tokens':
              newSettings.ai_max_tokens = parseSettingNumber(rawValue, 500);
              break;
          }
        });

        console.log('[AI Chat] Final settings:', newSettings);
        setSettings(newSettings);
      }
    } catch (err) {
      console.error('[AI Chat] Error parsing settings:', err);
    }
  }, []);

  // Load or create conversation
  const loadConversation = useCallback(async () => {
    if (!user?.id) return;

    try {
      // Try to find existing conversation
      const { data: existingConversation, error: convError } = await supabase
        .from('ai_conversations')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (convError) throw convError;

      if (existingConversation) {
        setConversation(existingConversation);

        // Load messages for this conversation
        const { data: messagesData, error: msgError } = await supabase
          .from('ai_messages')
          .select('*')
          .eq('conversation_id', existingConversation.id)
          .order('created_at', { ascending: true });

        if (msgError) throw msgError;
        setMessages(messagesData || []);
      }
    } catch (err) {
      console.error('Error loading conversation:', err);
      setError(t('errors.conversationLoadFailed'));
    }
  }, [user?.id, t]);

  // Load usage for current month
  const loadUsage = useCallback(async () => {
    if (!user?.id) return;

    const currentMonth = new Date().toISOString().slice(0, 7); // "2024-01"

    try {
      const { data, error: usageError } = await supabase
        .from('ai_usage')
        .select('*')
        .eq('user_id', user.id)
        .eq('month', currentMonth)
        .maybeSingle();

      if (usageError) throw usageError;

      if (data) {
        setUsage(data);
      } else {
        // Create new usage record
        const { data: newUsage, error: insertError } = await supabase
          .from('ai_usage')
          .insert({
            user_id: user.id,
            month: currentMonth,
            questions_count: 0,
            tokens_used: 0,
          })
          .select()
          .single();

        if (insertError) throw insertError;
        setUsage(newUsage);
      }
    } catch (err) {
      console.error('Error loading usage:', err);
    }
  }, [user?.id]);

  // Single effect to load everything in sequence to avoid race conditions
  useEffect(() => {
    const load = async () => {
      // Increment request counter to track this specific load request
      const currentRequest = ++loadRequestRef.current;

      setIsLoading(true);
      setError(null);

      try {
        // Step 1: Load subscription tier first and get the actual value
        const tier = await loadSubscriptionTier();

        // Check if this request is still the latest one
        if (currentRequest !== loadRequestRef.current) {
          console.log('Skipping stale settings load');
          return;
        }

        // Step 2: Load settings with the correct tier, conversation, and usage in parallel
        await Promise.all([
          loadSettings(tier), // Use the returned tier, not the state
          loadConversation(),
          loadUsage(),
        ]);

        // Only update loading state if this is still the latest request
        if (currentRequest === loadRequestRef.current) {
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Error in initial load:', err);
        if (currentRequest === loadRequestRef.current) {
          setError(t('errors.dataLoadFailed'));
          setIsLoading(false);
        }
      }
    };

    if (user?.id) {
      load();
    }
  }, [user?.id, loadSubscriptionTier, loadSettings, loadConversation, loadUsage]);

  // Update existing conversation with pregnancy_id if missing
  const updateConversationPregnancyId = useCallback(async (conv: AIConversation) => {
    if (!conv.pregnancy_id && currentPregnancy?.id) {
      const { error: updateError } = await supabase
        .from('ai_conversations')
        .update({ pregnancy_id: currentPregnancy.id })
        .eq('id', conv.id);

      if (!updateError) {
        const updatedConv = { ...conv, pregnancy_id: currentPregnancy.id };
        setConversation(updatedConv);
        return updatedConv;
      }
    }
    return conv;
  }, [currentPregnancy?.id]);

  // Create conversation if needed - waits for pregnancy to be loaded
  const ensureConversation = async (): Promise<AIConversation> => {
    // If conversation exists, check if we need to update pregnancy_id
    if (conversation) {
      return await updateConversationPregnancyId(conversation);
    }

    // Wait a bit for pregnancy to load if not yet available
    let pregnancyId = currentPregnancy?.id || null;

    // If no pregnancy yet but user exists, try to fetch it directly
    if (!pregnancyId && user?.id) {
      // First try: User created the pregnancy
      const { data: pregnancyData } = await supabase
        .from('pregnancies')
        .select('id')
        .eq('created_by', user.id)
        .eq('is_active', true)
        .maybeSingle();

      if (pregnancyData?.id) {
        pregnancyId = pregnancyData.id;
      } else {
        // Second try: User is a connected partner
        const { data: partnerData } = await supabase
          .from('partner_connections')
          .select('pregnancy_id')
          .eq('user_2_id', user.id)
          .eq('status', 'active')
          .maybeSingle();

        if (partnerData?.pregnancy_id) {
          pregnancyId = partnerData.pregnancy_id;
        }
      }
    }

    const { data, error: createError } = await supabase
      .from('ai_conversations')
      .insert({
        user_id: user!.id,
        pregnancy_id: pregnancyId,
        title: t('conversation.newTitle'),
      })
      .select()
      .single();

    if (createError) throw createError;
    setConversation(data);
    return data;
  };

  // Send message
  const sendMessage = async (content: string) => {
    if (!user?.id || isSending || isLimitReached) return;

    setIsSending(true);
    setError(null);

    try {
      // Ensure conversation exists
      const conv = await ensureConversation();

      // Create user message
      const userMessage: AIMessage = {
        id: crypto.randomUUID(),
        conversation_id: conv.id,
        role: 'user',
        content,
        tokens_used: null,
        is_flagged: false,
        created_at: new Date().toISOString(),
      };

      // Optimistic update
      setMessages((prev) => [...prev, userMessage]);

      // Save user message to DB
      const { error: userMsgError } = await supabase
        .from('ai_messages')
        .insert({
          id: userMessage.id,
          conversation_id: conv.id,
          role: 'user',
          content,
        });

      if (userMsgError) throw userMsgError;

      // Get response based on mode
      let responseContent: string;
      let tokensUsed = 0;

      if (settings.ai_chat_mode === 'mock') {
        // Use mock response (locale-aware)
        const mockLocale = (i18n.language === 'en' ? 'en' : 'de') as 'de' | 'en';
        const { response } = getMockResponse(content, mockLocale);
        responseContent = response;
        tokensUsed = Math.ceil(response.length / 4); // Rough estimate

        // Simulate typing delay
        const delay = getTypingDelay(response.length);
        await new Promise((resolve) => setTimeout(resolve, delay));
      } else {
        // Call Supabase Edge Function
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData?.session?.access_token;

        if (!token) {
          throw new Error('No authentication token');
        }

        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-chat`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              conversationId: conv.id,
              message: content,
              locale: i18n.language === 'en' ? 'en' : 'de',
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          if (errorData.code === 'LIMIT_REACHED') {
            throw new Error(t('errors.limitReached'));
          }
          throw new Error(errorData.error || t('errors.apiFailed'));
        }

        const apiResult = await response.json();
        responseContent = apiResult.response;
        tokensUsed = apiResult.tokensUsed || 0;
      }

      // Create assistant message
      const assistantMessage: AIMessage = {
        id: crypto.randomUUID(),
        conversation_id: conv.id,
        role: 'assistant',
        content: responseContent,
        tokens_used: tokensUsed,
        is_flagged: false,
        created_at: new Date().toISOString(),
      };

      // Save assistant message to DB
      const { error: assistantMsgError } = await supabase
        .from('ai_messages')
        .insert({
          id: assistantMessage.id,
          conversation_id: conv.id,
          role: 'assistant',
          content: responseContent,
          tokens_used: tokensUsed,
        });

      if (assistantMsgError) throw assistantMsgError;

      // Update messages
      setMessages((prev) => [...prev, assistantMessage]);

      // Update usage
      if (usage) {
        const { error: usageError } = await supabase
          .from('ai_usage')
          .update({
            questions_count: usage.questions_count + 1,
            tokens_used: usage.tokens_used + tokensUsed,
          })
          .eq('id', usage.id);

        if (usageError) throw usageError;

        setUsage({
          ...usage,
          questions_count: usage.questions_count + 1,
          tokens_used: usage.tokens_used + tokensUsed,
        });
      }

      // Update conversation timestamp
      await supabase
        .from('ai_conversations')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', conv.id);
    } catch (err) {
      console.error('Error sending message:', err);
      setError(t('errors.sendFailed'));
      // Remove optimistic update on error
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setIsSending(false);
    }
  };

  // Clear conversation
  const clearConversation = async () => {
    if (!conversation) return;

    try {
      // Delete messages
      await supabase.from('ai_messages').delete().eq('conversation_id', conversation.id);

      // Delete conversation
      await supabase.from('ai_conversations').delete().eq('id', conversation.id);

      setMessages([]);
      setConversation(null);
    } catch (err) {
      console.error('Error clearing conversation:', err);
      setError(t('errors.clearFailed'));
    }
  };

  return {
    messages,
    conversation,
    usage,
    settings,
    subscriptionTier,
    isLoading,
    isSending,
    error,
    sendMessage,
    clearConversation,
    isLimitReached,
    remainingQuestions,
    isUnlimited,
  };
}
